import { Injectable, signal } from '@angular/core';

/** Options for a single confirmation prompt. */
export interface ConfirmOptions {
  /** Dialog heading. Defaults to "Please confirm". */
  title?: string;
  /** Body message shown to the user. */
  message: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** When true, the confirm button is styled as destructive (red). */
  danger?: boolean;
}

interface ConfirmState extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

/**
 * App-wide confirmation dialog. Replaces the native `window.confirm()` with a
 * design-language modal. A single `<app-confirm-host />` (mounted in the shell)
 * renders whatever request is active; callers just `await confirm(...)`.
 *
 *   if (await this.confirm.confirm({ message: 'Delete this?', danger: true })) { ... }
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly state = signal<ConfirmState | null>(null);

  /** The active request (or null). Consumed by `ConfirmHost`. */
  readonly current = this.state.asReadonly();

  /** Opens the dialog and resolves to the user's choice. */
  confirm(options: ConfirmOptions): Promise<boolean> {
    // If a prompt is already open, resolve it as cancelled before replacing it.
    this.state()?.resolve(false);
    return new Promise<boolean>((resolve) => {
      this.state.set({ ...options, resolve });
    });
  }

  /** Confirm the active prompt. */
  accept(): void {
    const state = this.state();
    if (!state) return;
    this.state.set(null);
    state.resolve(true);
  }

  /** Dismiss the active prompt (cancel / backdrop / Escape). */
  dismiss(): void {
    const state = this.state();
    if (!state) return;
    this.state.set(null);
    state.resolve(false);
  }
}
